import React, { useState, forwardRef } from 'react';
import axios from 'axios';
import classNames from 'classnames';
import { logger } from '@fiverr-private/obs';
import { raiseToast } from '@fiverr-private/toast';
import { I18n, translate } from '@fiverr-private/i18n-react';
import { Button } from '@fiverr-private/button';
import { PaperPlaneIcon } from '@fiverr-private/icons';
import {
    Modal,
    ModalBody,
    ModalCloseBtn,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalTrigger,
    useModalContext,
} from '@fiverr-private/overlay';
import { BsmData, BusinessType } from '../types';
import { SendMessageContext, UserRole } from '../../types';
import { reportMessageSent } from '../../utils/events/events';
import { useUserRole } from '../../hooks/useUserRole/useUserRole';
import plane from '../../assets/plane.svg';
import { Recipient } from './Recipient';
import { OpeningHours } from './OpeningHours';

import styles from './SendMessageModal.module.scss';

export const MODAL_TRANSLATIONS_KEY = 'help_menu.modal';

const getTranslationsKey = (isMatching = false) => `${MODAL_TRANSLATIONS_KEY}${isMatching ? '.matching' : ''}`;

interface SuccessModalContentProps {
    displayName?: string;
    isMatching: boolean;
    businessType: BusinessType;
}

const SuccessModalContent = forwardRef<HTMLDivElement, SuccessModalContentProps>(
    ({ displayName, isMatching, businessType }, ref) => {
        const { close } = useModalContext();
        const translationsKey = getTranslationsKey(isMatching);

        return (
            <ModalContent ref={ref} renderWhenClosed size="sm">
                <ModalHeader>
                    <ModalCloseBtn />
                </ModalHeader>
                <ModalBody>
                    <img className={styles.planeIcon} src={plane} />
                    <div className={styles.successTitleContainer}>
                        <h3 className="m-b-8">
                            <I18n k={`${translationsKey}.success_title`} />
                        </h3>
                        <div>
                            {businessType === 'strategic' ? (
                                <I18n
                                    k={`${translationsKey}.success_subtitle_strategic`}
                                    params={{ bsmName: displayName, day: 1 }}
                                />
                            ) : (
                                <I18n k={`${translationsKey}.success_subtitle_classic`} />
                            )}
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button className={styles.successCta} onClick={close}>
                        <I18n k={`${translationsKey}.success_cta`} />
                    </Button>
                </ModalFooter>
            </ModalContent>
        );
    }
);

SuccessModalContent.displayName = 'SuccessModalContent';

interface SendMessageModalProps {
    bsm?: BsmData;
    trigger: React.ReactNode;
    onOpen?: () => void;
    isMatching?: boolean;
    businessType: BusinessType;
    source?: string;
    userRole?: UserRole;
    context?: SendMessageContext;
    onSendSuccess?: () => void;
    onCloseAfterSuccess?: () => void;
}

export const SendMessageModal = forwardRef<HTMLDivElement, SendMessageModalProps>(
    (
        {
            trigger,
            bsm,
            onOpen,
            isMatching = false,
            businessType,
            source,
            userRole: initialUserRole,
            context,
            onSendSuccess,
            onCloseAfterSuccess,
        },
        ref
    ) => {
        const [emailSent, setEmailSent] = useState(false);
        const [subject, setSubject] = useState('');
        const [message, setMessage] = useState('');
        const translationsKey = getTranslationsKey(isMatching);
        const { userRole } = useUserRole({ source: source || '', initialValue: initialUserRole });

        const handleMessageSent = () => {
            reportMessageSent({
                source,
                businessType,
                elementText: translate(`${translationsKey}.cta`) as string,
                bsmName: bsm?.display_name || null,
                userRole: userRole as UserRole,
            });
        };

        const onSubmit = async () => {
            const URL = '/business/api/business_success_manager/contact';
            try {
                await axios.post(URL, {
                    message,
                    is_asking_for_help: !isMatching,
                    subject: isMatching ? 'Find me a match for my project' : subject,
                    context,
                });
                onSendSuccess?.();
                setEmailSent(true);
            } catch (error) {
                logger.error(error as Error, {
                    description: `Failed to POST ${URL}`,
                });
                raiseToast({
                    message: translate(`${MODAL_TRANSLATIONS_KEY}.submit_error_msg`),
                    type: 'error',
                    location: 'top',
                });
            }
        };

        const onClose = () => {
            setSubject('');
            setMessage('');
            setEmailSent(false);

            if (emailSent) {
                onCloseAfterSuccess?.();
            }
        };

        const hasBsm = !!bsm;
        const messageInputPlaceholder = translate(
            `${translationsKey}.${hasBsm ? 'placeholder_bsm' : 'placeholder_no_bsm'}`,
            { params: { bsmName: bsm?.display_name } }
        );

        return (
            <Modal onOpen={onOpen} onClose={onClose}>
                <ModalTrigger as="div">{trigger}</ModalTrigger>
                {emailSent ? (
                    <SuccessModalContent
                        displayName={bsm?.display_name}
                        isMatching={isMatching}
                        businessType={businessType}
                    />
                ) : (
                    <ModalContent ref={ref} closeOnClickOutside={false}>
                        <ModalHeader>
                            <I18n k={`${translationsKey}.title`} />
                            <ModalCloseBtn />
                        </ModalHeader>
                        <ModalBody>
                            <Recipient bsm={bsm} />
                            <label className={styles.recipientContainer}>
                                <span className={classNames(styles.text, 'tbody-5 p-r-12')}>
                                    <I18n k={`${translationsKey}.subject_placeholder`} />
                                </span>
                                {isMatching ? (
                                    <span className={styles.matchingRecipientValue}>
                                        <I18n k={`${translationsKey}.subject_value`} />
                                    </span>
                                ) : (
                                    <input
                                        onChange={(e) => setSubject(e.target.value)}
                                        className={classNames(styles.subjectInput, 'tbody-6')}
                                    />
                                )}
                            </label>
                            <textarea
                                className={classNames(styles.textarea, 'tbody-6')}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder={messageInputPlaceholder}
                                name="message_input"
                            ></textarea>
                            {businessType === 'strategic' && <OpeningHours />}
                        </ModalBody>
                        <ModalFooter>
                            <div className={styles.footerContainer}>
                                <Button
                                    disabled={(!isMatching ? Boolean(!subject.length) : false) || !message.length}
                                    onClick={() => {
                                        handleMessageSent();
                                        onSubmit();
                                    }}
                                >
                                    <PaperPlaneIcon color="currentColor" />
                                    <I18n k={`${translationsKey}.cta`} />
                                </Button>
                            </div>
                        </ModalFooter>
                    </ModalContent>
                )}
            </Modal>
        );
    }
);

SendMessageModal.displayName = 'SendMessageModal';
